// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { bootstrapSharedActionCreators, dealerSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import type { ReduxNext } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import {
    APPLY_TEMPLATE_PRODUCTS,
    DELETE_DEAL_TEMPLATE,
    GET_DEAL_TEMPLATES,
    GET_MANUAL_DEAL_TEMPLATES,
    INIT_TEMPLATE_FROM_DEAL_SUCCESS,
    MANUALLY_APPLY_DEAL_TEMPLATE,
    MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST,
    REVERT_MANUALLY_APPLIED_TEMPLATE,
    SAVE_DEAL_TEMPLATE,
    UPDATE_DEAL_TEMPLATE
} from '../actionTypes/dealTemplateActionTypes';
import type { TemplateSavePayload } from '../actions/dealTemplateActionCreators';
import {
    applyTemplateFromDealRSAA,
    deleteTemplatesRSAA,
    getManualTemplatesRSAA,
    getTemplatesRSAA,
    saveTemplateRSAA,
    updateTemplateRSAA
} from '../actions/dealTemplateActionCreators';
import * as bridgeUserSelectors from '../selectors/bridgeUserSelectors';
import * as configSelectors from '../selectors/config';
import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as quoteSelectors from '../selectors/quoteSelectors';
import * as vehicleProtectionSelectors from '../selectors/vehicleProtectionSelectors';

import { buildTemplateSavePayload } from '../utils/offerPayloadUtil';
import * as offerActionCreators from '../actions/offerActionCreators';
import * as vehicleProtectionActionCreators from '../actions/vehicleProtectionActionCreators';
import { OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import {
    fetchTemplateScenario,
    removeAlternateScenariosFromState,
    withPrimaryOfferToggles,
    withoutScenarioFeatureToggles
} from '../actions/multiScenarioActionCreators';
import { FETCH_RATES_SUCCESS } from '../actionTypes/vehicleProtectionActionTypes';
import { APPLY_ORIGINAL_TEMPLATE_PRODUCTS } from '../actionTypes/dealTemplateActionTypes';
import { RATE_REQUEST_STATUSES } from '../types/vehicleProtectionTypes';
import { FALLBACK_TO_TARGET_PRODUCTS_COUNT } from '@makemydeal/dr-dash-types';
import type { RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { getDealXgId } from '../selectors/offerRedux';
import { logManualApplyTemplateEvent } from '../utils/multiScenarioTemplateAnalytics';

export const middleware = (store: any) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case SAVE_DEAL_TEMPLATE: {
            const state = store.getState();
            const offerSavePayload = buildTemplateSavePayload(state, undefined, action.payload?.selectedScopeIds);
            const savePaymentTerms = dealTemplateSelectors.getSavePaymentTerms(state);

            const user = {
                bridgeId: bridgeUserSelectors.getBridgeId(state),
                name: `${bridgeUserSelectors.getBridgeFirstName(state)} ${bridgeUserSelectors.getBridgeLastName(state)}`
            };
            const activeDealType = offerReduxSelectors.getCurrentOfferType(state);
            const { selectedScopeIds: _selectedScopeIds, ...templateParams } = action.payload || {};
            const templateSavePayload: TemplateSavePayload = {
                ...offerSavePayload,
                user,
                savePaymentTerms,
                activeDealType,
                template: templateParams
            };
            const saveOfferAction = saveTemplateRSAA({
                state,
                source: action.meta?.source,
                templateSavePayload
            });

            store.dispatch(saveOfferAction);

            break;
        }
        case GET_DEAL_TEMPLATES: {
            const state = store.getState();

            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const getTemplatesAction = getTemplatesRSAA(dealerId, bffEndpoint);

            store.dispatch(getTemplatesAction);
            break;
        }
        case GET_MANUAL_DEAL_TEMPLATES: {
            const state = store.getState();

            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);
            const queryParams = action.payload?.queryParams ?? {};
            const getManualTemplatesAction = getManualTemplatesRSAA(dealerId, bffEndpoint, queryParams);

            store.dispatch(getManualTemplatesAction);
            break;
        }
        case DELETE_DEAL_TEMPLATE: {
            const { template } = action.payload;
            const state = store.getState();

            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const deleteTemplateAction = deleteTemplatesRSAA(dealerId, template, bffEndpoint);

            store.dispatch(deleteTemplateAction);
            break;
        }
        case UPDATE_DEAL_TEMPLATE: {
            const { template, meta } = action.payload;
            const state = store.getState();

            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);
            const updatedBy = bridgeUserSelectors.getBridgeId(state);
            const updatedByName = `${bridgeUserSelectors.getBridgeFirstName(state)} ${bridgeUserSelectors.getBridgeLastName(
                state
            )}`;

            const updateTemplateAction = updateTemplateRSAA(
                dealerId,
                {
                    ...template,
                    updatedByName,
                    updatedBy
                },
                bffEndpoint,
                meta
            );

            store.dispatch(updateTemplateAction);
            break;
        }

        case MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST: {
            const { templateId, templateName } = action.payload;
            const state = store.getState();

            if (quoteSelectors.getIsQuoteModeWithoutDealXgRecord(state)) {
                logManualApplyTemplateEvent(state, { templateId, templateName });
                store.dispatch(applyTemplateFromDealRSAA(templateId));
                break;
            }

            store.dispatch(
                offerActionCreators.saveOffer({
                    isDraft: true,
                    originalAction: MANUALLY_APPLY_DEAL_TEMPLATE,
                    templateId
                })
            );

            // NR: Track multi-scenario template manual application
            const manualApplyState = store.getState();
            logManualApplyTemplateEvent(manualApplyState, {
                templateId,
                templateName
            });

            break;
        }

        case REVERT_MANUALLY_APPLIED_TEMPLATE: {
            // Template apply persists into a template-scoped draft slot in DXG (path /init/{dealXgId}/template/{templateId}),
            // leaving the user's primary draft untouched. Reload it WITHOUT templateId to restore the pre-template state.
            store.dispatch(
                bootstrapSharedActionCreators.fetchBootstrapData(MANAGER_VIEW_APP_PREFIX, {
                    loadDraftScenario: true
                })
            );

            break;
        }

        case OFFER_SAVE_SUCCESS: {
            if (action.meta?.originalAction !== MANUALLY_APPLY_DEAL_TEMPLATE) {
                return;
            }

            const templateId = action.meta.templateId;

            if (!templateId) return;

            const state = store.getState();
            const isPrimaryScope = scopedStateSelectors.isCurrentScopePrimary(state);

            if (isPrimaryScope) {
                store.dispatch(removeAlternateScenariosFromState());

                store.dispatch(
                    bootstrapSharedActionCreators.fetchBootstrapData(MANAGER_VIEW_APP_PREFIX, {
                        loadDraftScenario: true,
                        templateId
                    })
                );

                return;
            }

            const currentScopeId = scopedStateSelectors.getCurrentScopeId(state);
            const currentNonPrimaryScenario = action.payload.scenarioIds?.[`scopeId_${currentScopeId}`].replace(
                'scenarioId_',
                ''
            ) as string;

            if (!currentNonPrimaryScenario) return;

            store.dispatch(fetchTemplateScenario(offerReduxSelectors.getDealXgId(state), currentNonPrimaryScenario, templateId));

            break;
        }

        case FETCH_RATES_SUCCESS: {
            const state = store.getState();
            const { status, products } = action.payload;
            const isApplyingProducts = vehicleProtectionSelectors.isApplyingTemplateProducts(state);

            if (status === RATE_REQUEST_STATUSES.PENDING) {
                const { retryNumber = 0 } = action.meta || {};

                if (isApplyingProducts && retryNumber === FALLBACK_TO_TARGET_PRODUCTS_COUNT) {
                    store.dispatch({
                        type: APPLY_ORIGINAL_TEMPLATE_PRODUCTS
                    });
                }
            }

            if (isApplyingProducts && status === RATE_REQUEST_STATUSES.FINISHED) {
                store.dispatch({
                    type: APPLY_TEMPLATE_PRODUCTS,
                    payload: {
                        products
                    }
                });
            }

            break;
        }

        case APPLY_TEMPLATE_PRODUCTS: {
            const state = store.getState();
            const { products } = action.payload;

            const recommendedProducts = products?.filter((product: RatedProtectionProduct) => product.recommended) || [];

            recommendedProducts.forEach((product: RatedProtectionProduct, index: number) => {
                logNewRelicPageAction('MV:Deal Template - product close to the template has been recommended', {
                    templateId: dealTemplateSelectors.getAppliedDealTemplateId(state),
                    dealerId: dealerSelectors.getDealerId(state),
                    dealXgId: getDealXgId(state),
                    platform: 'MV',
                    productCode: product.productCode,
                    productProviderName: product.content?.providerName,
                    productProviderCode: product.providerCode,
                    productCategoryCode: product.productCategoryCode,
                    targetProductCode: product.targetProductCode
                });

                const isLastRecomendedProduct = index === recommendedProducts.length - 1;

                store.dispatch(
                    vehicleProtectionActionCreators.selectVppProduct(product.productCode, {
                        skipPayment: !isLastRecomendedProduct
                    })
                );
            });
            break;
        }

        case APPLY_ORIGINAL_TEMPLATE_PRODUCTS: {
            const targetProducts = vehicleProtectionSelectors.getTargetProducts(store.getState());

            targetProducts.forEach((product) => {
                logNewRelicPageAction('MV:Deal Template - fallback to original template product', {
                    templateId: dealTemplateSelectors.getAppliedDealTemplateId(store.getState()),
                    dealerId: dealerSelectors.getDealerId(store.getState()),
                    dealXgId: getDealXgId(store.getState()),
                    platform: 'MV',
                    productCode: product.productCode,
                    productProviderName: product.productProviderName,
                    productProviderCode: product.providerCode,
                    productCategoryCode: product.productCategoryCode
                });
            });

            break;
        }

        case INIT_TEMPLATE_FROM_DEAL_SUCCESS: {
            const offerData = action.payload;

            if (!offerData) break;

            store.dispatch({
                type: initActionTypes.INIT_PENCIL_SUCCESS,
                payload: withPrimaryOfferToggles(withoutScenarioFeatureToggles(offerData), store.getState())
            });

            break;
        }

        default:
            break;
    }
};
